import React, { useState } from 'react';
import {
  Musician, MusicianStatus, OrganWork,
  addOrganWork, updateOrganWork, deleteOrganWork,
  addMusician, updateMusician, deleteMusician,
} from '../../services/seasonViewService';
import { STATUS, STATUS_ORDER, StatusKey, TERMINAL } from './tokens';

/**
 * The Repertoire and Playing lanes, editable in place. The same components run
 * in the season grid and on the Sunday page — a season view you can only read
 * sends you somewhere else to change one thing, which is the trip this page
 * exists to save.
 */

const SLOTS = ['Prelude', 'Postlude'];

/* ---------------------------------------------------------------- organ -- */

export const OrganLane: React.FC<{
  serviceId: number | null;
  works: OrganWork[];
  onChanged: () => void;
}> = ({ serviceId, works, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [slot, setSlot] = useState('Prelude');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => { setAdding(false); setEditing(null); setText(''); setSlot('Prelude'); };

  // One box. A prelude is a line Jack types, not a catalogue entry, and has no
  // composer record behind it (2026-08-17).
  const save = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      if (editing !== null) await updateOrganWork(editing, { slot, work: text.trim() });
      else if (serviceId) await addOrganWork(serviceId, { slot, work: text.trim() });
      reset();
      onChanged();
    } finally { setBusy(false); }
  };

  const startEdit = (o: OrganWork) => { setEditing(o.id); setSlot(o.slot); setText(o.work); setAdding(false); };

  const form = (
    <div className="sv-form">
      <select className="sv-field sv-field--slot" value={slot} onChange={e => setSlot(e.target.value)}>
        {SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <input
        className="sv-field" autoFocus placeholder="Prelude in C, BWV 547 — Bach"
        value={text} onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') reset(); }}
      />
      <div className="sv-form-actions">
        <button className="sv-linkbtn" onClick={reset}>cancel</button>
        <button className="sv-add" disabled={busy || !text.trim()} onClick={save}>save</button>
      </div>
    </div>
  );

  return (
    <>
      {works.map(o => editing === o.id ? (
        <div key={o.id}>{form}</div>
      ) : (
        <div key={o.id} className="sv-organ-item sv-editable">
          <div className="sv-organ-slot">{o.slot}</div>
          <div className="sv-organ-work">{o.work}</div>
          <div className="sv-row-actions">
            <button className="sv-linkbtn" onClick={() => startEdit(o)}>edit</button>
            <button className="sv-linkbtn" onClick={async () => { await deleteOrganWork(o.id); onChanged(); }}>
              remove
            </button>
          </div>
        </div>
      ))}

      {adding && editing === null && form}

      {!adding && editing === null && (
        works.length === 0
          ? <button className="sv-empty sv-empty--action" disabled={!serviceId} onClick={() => setAdding(true)}>
              prelude · postlude — open
            </button>
          : <button className="sv-add sv-add--inline" disabled={!serviceId} onClick={() => setAdding(true)}>+ add</button>
      )}
    </>
  );
};

/* --------------------------------------------------------------- people -- */

export const PlayingLane: React.FC<{
  serviceId: number | null;
  people: Musician[];
  suggestions: { name: string; instrument: string | null }[];
  onChanged: () => void;
}> = ({ serviceId, people, suggestions, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [inst, setInst] = useState('');
  const [status, setStatus] = useState<MusicianStatus>('asked');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setAdding(false); setEditing(null);
    setName(''); setInst(''); setStatus('asked'); setNote('');
  };

  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        instrument: inst.trim() || null,
        status,
        note: note.trim() || null,
      };
      if (editing !== null) await updateMusician(editing, body);
      else if (serviceId) await addMusician(serviceId, body);
      reset();
      onChanged();
    } finally { setBusy(false); }
  };

  const startEdit = (m: Musician) => {
    setEditing(m.id); setAdding(false);
    setName(m.name); setInst(m.instrument || ''); setStatus(m.status); setNote(m.note || '');
  };

  // A name used before fills in the instrument it was used with.
  const onNameChange = (v: string) => {
    setName(v);
    const hit = suggestions.find(s => s.name.toLowerCase() === v.trim().toLowerCase());
    if (hit && hit.instrument && !inst) setInst(hit.instrument);
  };

  const form = (
    <div className="sv-form">
      <input
        className="sv-field" autoFocus placeholder="Name" list="sv-musician-names"
        value={name} onChange={e => onNameChange(e.target.value)}
      />
      <datalist id="sv-musician-names">
        {suggestions.map((s, i) => <option key={i} value={s.name} />)}
      </datalist>
      <input
        className="sv-field" placeholder="Instrument"
        value={inst} onChange={e => setInst(e.target.value)}
      />
      <select
        className="sv-field" value={status}
        onChange={e => setStatus(e.target.value as MusicianStatus)}
      >
        {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS[s].label}</option>)}
      </select>
      <input
        className="sv-field" placeholder="Note — e.g. asked 2 Sep, no answer"
        value={note} onChange={e => setNote(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') reset(); }}
      />
      <div className="sv-form-actions">
        <button className="sv-linkbtn" onClick={reset}>cancel</button>
        <button className="sv-add" disabled={busy || !name.trim()} onClick={save}>save</button>
      </div>
    </div>
  );

  return (
    <>
      {people.map(m => {
        if (editing === m.id) return <div key={m.id}>{form}</div>;
        const key = m.status as StatusKey;
        const st = STATUS[key] || STATUS.idea;
        return (
          <div key={m.id} className={`sv-person sv-editable${TERMINAL.includes(key) ? ' sv-terminal' : ''}`}>
            <span className="sv-mark cl-status-mark" style={{ color: st.color }}>{st.mark}</span>
            <div className="sv-pbody">
              <span className="sv-pname">{m.name}</span>
              {m.instrument && <span className="sv-pinst"> · {m.instrument}</span>}
              {m.note && <span className="sv-pnote cl-status-note">{m.note}</span>}
            </div>
            {/* Status is the field that changes most, so it changes without
                opening the row for editing. */}
            <select
              className="sv-status-select"
              value={m.status}
              onChange={async e => { await updateMusician(m.id, { status: e.target.value as MusicianStatus }); onChanged(); }}
            >
              {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS[s].label}</option>)}
            </select>
            <div className="sv-row-actions">
              <button className="sv-linkbtn" onClick={() => startEdit(m)}>edit</button>
              <button className="sv-linkbtn" onClick={async () => { await deleteMusician(m.id); onChanged(); }}>
                remove
              </button>
            </div>
          </div>
        );
      })}

      {adding && editing === null && form}

      {!adding && editing === null && (
        people.length === 0
          ? <button className="sv-empty sv-empty--action" disabled={!serviceId} onClick={() => setAdding(true)}>
              organ &amp; choir only
            </button>
          : <button className="sv-add sv-add--inline" disabled={!serviceId} onClick={() => setAdding(true)}>+ add</button>
      )}
    </>
  );
};
